import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Typography,
  Box,
  Chip,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  IntakeFormType,
  CreateIntakeFormTypeData,
  UpdateIntakeFormTypeData,
} from '../services/intakeFormType.service';
import { getServiceTypes } from '../services/serviceType.service';

interface ServiceType {
  id: number;
  name: string;
  short_code: string;
  description?: string;
  price: number;
}

interface IntakeFormTypeFormProps {
  initialValues?: IntakeFormType;
  onSubmit: (data: CreateIntakeFormTypeData | UpdateIntakeFormTypeData) => void;
  loading?: boolean;
}

const IntakeFormTypeForm: React.FC<IntakeFormTypeFormProps> = ({
  initialValues,
  onSubmit,
  loading = false,
}) => {
  const [formData, setFormData] = useState<CreateIntakeFormTypeData>({
    name: '',
    short_code: '',
    description: '',
    is_active: true,
    sort_order: 0,
    service_types: [],
  });

  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!initialValues;

  useEffect(() => {
    loadServiceTypes();
    if (initialValues) {
      setFormData({
        name: initialValues.name,
        short_code: initialValues.short_code,
        description: initialValues.description || '',
        is_active: initialValues.is_active,
        sort_order: initialValues.sort_order,
        service_types:
          initialValues.service_types?.map(st => ({
            id: st.id,
            is_required: st.pivot.is_required,
            sort_order: st.pivot.sort_order,
          })) || [],
      });
    }
  }, [initialValues]);

  const loadServiceTypes = async () => {
    try {
      const response = await getServiceTypes();
      setServiceTypes(response.data);
    } catch (error) {
      console.error('Failed to load service types:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (
        !file.name.toLowerCase().endsWith('.docx') &&
        !file.name.toLowerCase().endsWith('.doc')
      ) {
        setErrors(prev => ({
          ...prev,
          template_file: 'Please select a DOCX or DOC file',
        }));
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          template_file: 'File size must be less than 10MB',
        }));
        return;
      }

      setSelectedFile(file);
      setErrors(prev => ({
        ...prev,
        template_file: '',
      }));
    }
  };

  const handleServiceTypeToggle = (serviceTypeId: number) => {
    const existingMapping = formData.service_types?.find(
      st => st.id === serviceTypeId
    );

    if (existingMapping) {
      setFormData(prev => ({
        ...prev,
        service_types:
          prev.service_types?.filter(st => st.id !== serviceTypeId) || [],
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        service_types: [
          ...(prev.service_types || []),
          {
            id: serviceTypeId,
            is_required: false,
            sort_order: (prev.service_types?.length || 0) + 1,
          },
        ],
      }));
    }
  };

  const handleServiceTypeChange = (
    serviceTypeId: number,
    field: 'is_required' | 'sort_order',
    value: any
  ) => {
    setFormData(prev => ({
      ...prev,
      service_types:
        prev.service_types?.map(st =>
          st.id === serviceTypeId ? { ...st, [field]: value } : st
        ) || [],
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.short_code.trim()) {
      newErrors.short_code = 'Short code is required';
    } else if (!/^[A-Z_]+$/.test(formData.short_code)) {
      newErrors.short_code =
        'Short code must contain only uppercase letters and underscores';
    }

    if ((formData.sort_order || 0) < 0) {
      newErrors.sort_order = 'Sort order must be 0 or greater';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      template_file: selectedFile || undefined,
    };

    console.log('Form submit data:', submitData);
    console.log('Is editing:', isEditing);
    console.log('Initial values:', initialValues);

    if (isEditing && initialValues) {
      const updateData = {
        ...submitData,
        id: initialValues.id,
      };
      console.log('Update data:', updateData);
      (onSubmit as (data: UpdateIntakeFormTypeData) => void)(updateData);
    } else {
      console.log('Create data:', submitData);
      (onSubmit as (data: CreateIntakeFormTypeData) => void)(submitData);
    }
  };

  const getSelectedServiceType = (serviceTypeId: number) => {
    return formData.service_types?.find(st => st.id === serviceTypeId);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        {/* Basic Information */}
        <Typography variant='h6'>Basic Information</Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            label='Name'
            value={formData.name}
            onChange={e => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
          />
          <TextField
            fullWidth
            label='Short Code'
            value={formData.short_code}
            onChange={e =>
              handleInputChange('short_code', e.target.value.toUpperCase())
            }
            error={!!errors.short_code}
            helperText={
              errors.short_code ||
              'Use uppercase letters and underscores (e.g., LCP_STANDARD)'
            }
            required
          />
        </Box>

        <TextField
          fullWidth
          label='Description'
          value={formData.description}
          onChange={e => handleInputChange('description', e.target.value)}
          multiline
          rows={3}
        />

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label='Sort Order'
            type='number'
            value={formData.sort_order}
            onChange={e =>
              handleInputChange('sort_order', parseInt(e.target.value) || 0)
            }
            error={!!errors.sort_order}
            helperText={errors.sort_order}
            inputProps={{ min: 0 }}
            sx={{ width: 200 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={e => handleInputChange('is_active', e.target.checked)}
              />
            }
            label='Active'
          />
        </Box>

        {/* Template Upload */}
        <Divider />
        <Typography variant='h6'>Template File</Typography>

        <Box>
          <input
            accept='.docx,.doc'
            style={{ display: 'none' }}
            id='template-file-input'
            type='file'
            onChange={handleFileChange}
          />
          <label htmlFor='template-file-input'>
            <Button variant='outlined' component='span'>
              {selectedFile ? 'Change Template File' : 'Upload Template File'}
            </Button>
          </label>

          {selectedFile && (
            <Box sx={{ mt: 1 }}>
              <Chip
                label={selectedFile.name}
                onDelete={() => setSelectedFile(null)}
                deleteIcon={<DeleteIcon />}
                onClick={() => false}
              />
            </Box>
          )}

          {initialValues?.template_filename && !selectedFile && (
            <Box sx={{ mt: 1 }}>
              <Chip
                label={initialValues.template_filename}
                icon={<DownloadIcon />}
                onClick={() => false}
              />
            </Box>
          )}

          {errors.template_file && (
            <Alert severity='error' sx={{ mt: 1 }}>
              {errors.template_file}
            </Alert>
          )}

          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            Upload a DOCX or DOC template file (max 10MB). This template will be
            used for PDF generation.
          </Typography>
        </Box>

        {/* Service Type Mapping */}
        <Divider />
        <Typography variant='h6'>Service Type Mapping</Typography>
        <Typography variant='body2' color='text.secondary'>
          Select which service types this intake form should be available for:
        </Typography>

        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            {serviceTypes.map(serviceType => {
              const isSelected = !!getSelectedServiceType(serviceType.id);
              const mapping = getSelectedServiceType(serviceType.id);

              return (
                <Box
                  key={serviceType.id}
                  sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isSelected}
                          onChange={() =>
                            handleServiceTypeToggle(serviceType.id)
                          }
                        />
                      }
                      label={serviceType.name}
                    />

                    {isSelected && (
                      <>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={mapping?.is_required || false}
                              onChange={e =>
                                handleServiceTypeChange(
                                  serviceType.id,
                                  'is_required',
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label='Required'
                        />

                        <TextField
                          label='Sort Order'
                          type='number'
                          value={mapping?.sort_order || 1}
                          onChange={e =>
                            handleServiceTypeChange(
                              serviceType.id,
                              'sort_order',
                              parseInt(e.target.value) || 1
                            )
                          }
                          inputProps={{ min: 1 }}
                          size='small'
                          sx={{ width: 120 }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Paper>

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type='submit'
            variant='contained'
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {isEditing ? 'Update Intake Form Type' : 'Create Intake Form Type'}
          </Button>
        </Box>
      </Stack>
    </form>
  );
};

export default IntakeFormTypeForm;
